import React, { useState, useEffect, useMemo, useRef } from "react";
import Modal from "../../../../components/Modal";
import Icon from "../../../../components/Icon";
import ProviderIcon from "./ProviderIcon";
import {
  createIntegration,
  updateIntegration,
  validateConnection,
  buildFullWebhookUrl,
} from "../../services/IntegrationsService";

export default function IntegrationWizardModal({
  isOpen,
  onClose,
  availableProviders = [],
  initialProvider = "jira",
  existingIntegration = null,
  onSuccess,
}) {
  const isEditing = Boolean(existingIntegration);

  // Selected Provider ID (e.g. 'jira', 'servicenow')
  const [selectedProviderId, setSelectedProviderId] = useState(() => {
    if (existingIntegration?.provider) return existingIntegration.provider;
    if (initialProvider) return initialProvider;
    return availableProviders[0]?.id || "jira";
  });

  // Basic Info
  const [name, setName] = useState("");
  const [category, setCategory] = useState("itsm");
  const [generateToken, setGenerateToken] = useState(true);

  // Dynamic configuration key-values
  const [configValues, setConfigValues] = useState({});

  // Additional custom fields (key-value pairs)
  const [customFields, setCustomFields] = useState([]);

  // Save states
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [testSuccess, setTestSuccess] = useState(null);

  // Post-create success state
  const [createdResult, setCreatedResult] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedToken, setCopiedToken] = useState(false);
  const [copyError, setCopyError] = useState(null);

  // Active provider metadata from backend
  const activeProvider = useMemo(() => {
    return (
      availableProviders.find(
        (p) => p.id?.toLowerCase() === selectedProviderId?.toLowerCase()
      ) || {
        id: selectedProviderId,
        name: selectedProviderId
          ? selectedProviderId.charAt(0).toUpperCase() + selectedProviderId.slice(1)
          : "Integration",
        category: "itsm",
        description: "External system integration",
        config_fields: [],
      }
    );
  }, [availableProviders, selectedProviderId]);

  // Only render required connection credentials in primary form; optional fields stay available via custom parameters
  const fieldsToRender = useMemo(() => {
    return (activeProvider.config_fields || []).filter(
      (f) => f.required !== false
    );
  }, [activeProvider]);

  const prevIsOpenRef = useRef(isOpen);
  const prevEditingIdRef = useRef(existingIntegration?.id);

  useEffect(() => {
    const justOpened = isOpen && !prevIsOpenRef.current;
    const editingTargetChanged =
      isEditing && existingIntegration?.id !== prevEditingIdRef.current;

    prevIsOpenRef.current = isOpen;
    prevEditingIdRef.current = existingIntegration?.id;

    if (!isOpen) {
      setCreatedResult(null);
      setSaveError(null);
      setTestSuccess(null);
      setCopiedUrl(false);
      setCopiedToken(false);
      setCustomFields([]);
      return;
    }

    // Never reset form while the user is viewing the success screen
    if (createdResult) {
      return;
    }

    // Only populate form when modal first opens or editing target changes
    if (justOpened || editingTargetChanged) {
      if (existingIntegration) {
        setSelectedProviderId(existingIntegration.provider);
        setName(existingIntegration.name || "");
        setCategory(existingIntegration.category || "itsm");
        setConfigValues(existingIntegration.config || {});
      } else {
        const provId = initialProvider || availableProviders[0]?.id || "jira";
        setSelectedProviderId(provId);
        const prov = availableProviders.find((p) => p.id === provId);
        setName(prov ? `${prov.name} Integration` : "New Integration");
        setCategory(prov?.category || "itsm");
        setGenerateToken(true);

        // Prepopulate defaults from backend config_fields
        const initialCfg = {};
        if (prov && Array.isArray(prov.config_fields)) {
          prov.config_fields.forEach((f) => {
            if (f.default !== undefined && f.default !== null) {
              initialCfg[f.name] = f.default;
            } else {
              initialCfg[f.name] = "";
            }
          });
        }
        setConfigValues(initialCfg);
      }
      setSaveError(null);
      setTestSuccess(null);
      setCreatedResult(null);
      setCopiedUrl(false);
      setCopiedToken(false);
      setCustomFields([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, existingIntegration, initialProvider]);

  const handleProviderSelectChange = (newProvId) => {
    setSelectedProviderId(newProvId);
    setSaveError(null);
    setTestSuccess(null);
    const prov = availableProviders.find((p) => p.id === newProvId);
    if (!isEditing) {
      setName(prov ? `${prov.name} Integration` : `${newProvId} Integration`);
      setCategory(prov?.category || "itsm");

      const newCfg = {};
      if (prov && Array.isArray(prov.config_fields)) {
        prov.config_fields.forEach((f) => {
          newCfg[f.name] = f.default ?? "";
        });
      }
      setConfigValues(newCfg);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setSaveError(null);
    setTestSuccess(null);
    setConfigValues((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleAddCustomField = () => {
    setCustomFields((prev) => [...prev, { key: "", value: "" }]);
  };

  const handleCustomFieldChange = (index, keyOrVal, text) => {
    setCustomFields((prev) => {
      const copy = [...prev];
      copy[index][keyOrVal] = text;
      return copy;
    });
  };

  const handleRemoveCustomField = (index) => {
    setCustomFields((prev) => prev.filter((_, i) => i !== index));
  };

  const assembleFinalConfig = () => {
    const final = {};
    Object.entries(configValues).forEach(([k, v]) => {
      if (typeof v === "string") {
        let cleaned = v.trim();
        // If field represents a URL, ensure valid format
        if ((k === "url" || k === "base_url" || k === "instance_url") && cleaned) {
          if (!cleaned.startsWith("http://") && !cleaned.startsWith("https://")) {
            cleaned = `https://${cleaned}`;
          }
          cleaned = cleaned.replace(/\/+$/, "");
        }
        final[k] = cleaned;
      } else {
        final[k] = v;
      }
    });

    customFields.forEach(({ key, value }) => {
      if (key && key.trim()) {
        const cleanedKey = key.trim();
        const cleanedVal = typeof value === "string" ? value.trim() : value;
        final[cleanedKey] = cleanedVal;
      }
    });
    return final;
  };

  const runValidation = async (config) => {
    try {
      const res = await validateConnection({
        provider: selectedProviderId,
        config,
      });
      if (res && res.success === false) {
        return {
          valid: false,
          message: res.message || "Connection validation failed. Please check credentials.",
        };
      }
      return {
        valid: true,
        message: res?.message || "Connection verified successfully!",
      };
    } catch (err) {
      const msg = err.message || "";
      // If live ping is not yet supported for this provider type, allow it through
      if (msg.includes("not yet supported") || msg.includes("Live validation not yet supported")) {
        return { valid: true, message: msg, unsupported: true };
      }
      return {
        valid: false,
        message: msg || "Failed to contact integration endpoint. Please verify URL and credentials.",
      };
    }
  };

  const handleManualTest = async () => {
    setTestingConnection(true);
    setSaveError(null);
    setTestSuccess(null);
    try {
      const config = assembleFinalConfig();
      const res = await runValidation(config);
      if (res.valid) {
        setTestSuccess(res.message);
      } else {
        setSaveError(res.message);
      }
    } finally {
      setTestingConnection(false);
    }
  };

  const executeSave = async (config) => {
    if (isEditing) {
      const updated = await updateIntegration(existingIntegration.id, {
        name,
        config,
      });
      onSuccess(updated);
      onClose();
    } else {
      const payload = {
        name,
        provider: selectedProviderId,
        category,
        config,
        generate_webhook_token: generateToken,
      };
      const created = await createIntegration(payload);

      // Auto-copy full webhook URL to clipboard
      const webhookPath =
        created.webhook_url || `/webhooks/${created.provider}/${created.id}`;
      const fullUrl = buildFullWebhookUrl(webhookPath, created.webhook_token);
      try {
        if (navigator?.clipboard?.writeText && fullUrl) {
          await navigator.clipboard.writeText(fullUrl);
          setCopiedUrl(true);
          setTimeout(() => setCopiedUrl(false), 2500);
        }
      } catch (err) {
        console.warn("Auto-copy webhook URL failed:", err);
      }

      // Always show the success screen with the new integration data
      setCreatedResult(created);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    setTestSuccess(null);

    try {
      const config = assembleFinalConfig();

      // Pre-flight connection validation
      const validation = await runValidation(config);
      if (!validation.valid) {
        setSaveError(validation.message);
        setSaving(false);
        return;
      }

      await executeSave(config);
    } catch (err) {
      setSaveError(err.message || "Failed to save integration");
    } finally {
      setSaving(false);
    }
  };

  const handleBypassSave = async () => {
    setSaving(true);
    setSaveError(null);
    setTestSuccess(null);
    try {
      const config = assembleFinalConfig();
      await executeSave(config);
    } catch (err) {
      setSaveError(err.message || "Failed to save integration");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  // Post-Creation Token Success Screen
  if (createdResult) {
    const webhookPath =
      createdResult.webhook_url ||
      `/webhooks/${createdResult.provider}/${createdResult.id}`;
    const fullUrl = buildFullWebhookUrl(webhookPath, createdResult.webhook_token);

    const handleCopy = async (text, type) => {
      setCopyError(null);
      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          const textarea = document.createElement("textarea");
          textarea.value = text;
          textarea.style.position = "fixed";
          textarea.style.left = "-9999px";
          document.body.appendChild(textarea);
          textarea.focus();
          textarea.select();
          const success = document.execCommand("copy");
          document.body.removeChild(textarea);
          if (!success) throw new Error("Copy command failed");
        }

        if (type === "url") {
          setCopiedUrl(true);
          setTimeout(() => setCopiedUrl(false), 2000);
        } else if (type === "token") {
          setCopiedToken(true);
          setTimeout(() => setCopiedToken(false), 2000);
        }
      } catch (err) {
        console.error("Failed to copy", err);
        setCopyError("Clipboard access was blocked or unsupported. Please copy manually.");
        setTimeout(() => setCopyError(null), 3500);
      }
    };

    const handleDone = () => {
      if (createdResult) {
        onSuccess(createdResult);
      }
      setCreatedResult(null);
      onClose();
    };

    return (
      <Modal isOpen={isOpen} onClose={handleDone} title="Integration Connected" width={560}>
        <div className="integration-success-modal-content">
          {copyError && (
            <div
              className="copy-error-banner"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 12px",
                background: "#FEF2F2",
                border: "1px solid #FCA5A5",
                borderRadius: 6,
                color: "#B91C1C",
                fontSize: 12,
                marginBottom: 12,
              }}
            >
              <Icon name="alert" size={14} color="#DC2626" />
              <span>{copyError}</span>
            </div>
          )}

          <div className="success-banner-compact">
            <div className="success-icon-badge">
              <Icon name="checkCircle" size={22} color="#15803D" />
            </div>
            <div>
              <h3>{createdResult.name} Connected</h3>
              <p>Your incoming webhook data pipeline has been provisioned and is active.</p>
            </div>
          </div>

          <div className="callout-warning-token">
            <Icon name="shield" size={15} color="#B45309" />
            <span>
              <strong>Copy your secret token now.</strong> For security, this token cannot be displayed again.
            </span>
          </div>

          <div className="field-group">
            <label className="field-label">
              <Icon name="link" size={13} color="#2563EB" />
              Webhook Destination URL
            </label>
            <div className="copy-input-row">
              <input type="text" readOnly value={fullUrl} className="form-input code-text" />
              <button
                type="button"
                className={`btn-copy-action ${copiedUrl ? "btn-copied" : ""}`}
                onClick={() => handleCopy(fullUrl, "url")}
              >
                <Icon name={copiedUrl ? "check" : "copy"} size={13} />
                {copiedUrl ? "Copied!" : "Copy URL"}
              </button>
            </div>
          </div>

          {createdResult.webhook_token && (
            <div className="field-group">
              <label className="field-label">
                <Icon name="key" size={13} color="#7C3AED" />
                Raw Webhook Secret Token
              </label>
              <div className="copy-input-row">
                <input
                  type="text"
                  readOnly
                  value={createdResult.webhook_token}
                  className="form-input code-text"
                />
                <button
                  type="button"
                  className={`btn-copy-action ${copiedToken ? "btn-copied" : ""}`}
                  onClick={() => handleCopy(createdResult.webhook_token, "token")}
                >
                  <Icon name={copiedToken ? "check" : "copy"} size={13} />
                  {copiedToken ? "Copied!" : "Copy Token"}
                </button>
              </div>
            </div>
          )}

          {activeProvider.setup_instructions && (
            <div className="setup-guide-box">
              <div className="setup-guide-title">
                <Icon name="info" size={14} color="#475569" />
                <span>Setup Instructions for {activeProvider.name}</span>
              </div>
              <p className="setup-guide-text">{activeProvider.setup_instructions}</p>
            </div>
          )}

          <div className="modal-form-actions">
            <button type="button" className="btn-primary" onClick={handleDone}>
              Done
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? `Edit ${name || activeProvider.name}` : `Connect ${activeProvider.name}`}
      width={560}
    >
      <form onSubmit={handleSave} className="integration-wizard-form">
        {/* Compact Provider Banner */}
        <div className="integration-dialog-banner">
          <div className="dialog-brand-avatar">
            <ProviderIcon provider={activeProvider} size={22} />
          </div>
          <div className="dialog-brand-info">
            <div className="dialog-brand-title-row">
              <h4 className="dialog-brand-name">{activeProvider.name}</h4>
              <span className={`provider-category-badge category-${category.toLowerCase()}`}>
                {category.toUpperCase()}
              </span>
            </div>
            <p className="dialog-brand-desc">{activeProvider.description}</p>
          </div>
        </div>

        {saveError && (
          <div className="form-error-banner">
            <Icon name="alert" size={15} color="#DC2626" />
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 3 }}>
              <span>{saveError}</span>
              <button
                type="button"
                onClick={handleBypassSave}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  fontSize: 11,
                  color: "#991B1B",
                  textDecoration: "underline",
                  cursor: "pointer",
                  textAlign: "left",
                  width: "fit-content",
                }}
              >
                Save anyway without verifying credentials
              </button>
            </div>
            <button
              type="button"
              onClick={() => setSaveError(null)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "2px",
                color: "#DC2626",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 4,
              }}
              title="Dismiss error"
            >
              <Icon name="x" size={13} color="#DC2626" />
            </button>
          </div>
        )}

        {testSuccess && (
          <div className="form-success-banner">
            <Icon name="checkCircle" size={15} color="#15803D" />
            <span style={{ flex: 1 }}>{testSuccess}</span>
            <button
              type="button"
              onClick={() => setTestSuccess(null)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "2px",
                color: "#15803D",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 4,
              }}
              title="Dismiss"
            >
              <Icon name="x" size={13} color="#15803D" />
            </button>
          </div>
        )}

        {/* Basic Details: Provider & Integration Name */}
        <div className="form-row-2">
          <div className="form-group">
            <label className="form-label">
              Provider {isEditing && <span className="label-subtext">(fixed)</span>}
            </label>
            <select
              className="form-input"
              value={selectedProviderId}
              onChange={(e) => handleProviderSelectChange(e.target.value)}
              disabled={isEditing}
            >
              {availableProviders.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">
              Integration Name <span className="required-star">*</span>
            </label>
            <input
              type="text"
              required
              className="form-input"
              placeholder="e.g. Production Jira"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
        </div>

        {/* Credentials & Configuration Grid */}
        <div className="credentials-section">
          <div className="section-divider-label">Credentials & Settings</div>
          {fieldsToRender.length > 0 ? (
            <div className="form-fields-grid">
              {fieldsToRender.map((field) => {
                const isFullWidth = field.type === "url" || field.name === "url";
                return (
                  <div
                    key={field.name}
                    className={`form-group ${isFullWidth ? "col-span-full" : ""}`}
                  >
                    <label className="form-label">
                      {field.label} {field.required && <span className="required-star">*</span>}
                    </label>
                    <input
                      type={field.type || "text"}
                      required={field.required}
                      className="form-input"
                      placeholder={field.placeholder || ""}
                      value={configValues[field.name] ?? ""}
                      onChange={(e) => handleFieldChange(field.name, e.target.value)}
                    />
                    {field.help_text && (
                      <span className="field-hint">{field.help_text}</span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="field-hint" style={{ margin: "4px 0" }}>
              No default credentials required for this provider. Add custom parameters below if needed.
            </p>
          )}
        </div>

        {/* Custom Parameters Section (Collapsible / On-Demand) */}
        {customFields.length === 0 ? (
          <div className="custom-params-toggle-row">
            <button
              type="button"
              className="btn-add-param-link"
              onClick={handleAddCustomField}
            >
              <Icon name="plus" size={13} color="#2563EB" />
              <span>Add custom parameter</span>
            </button>
          </div>
        ) : (
          <div className="custom-params-container">
            <div className="custom-params-header">
              <span className="params-label">Custom Parameters</span>
              <button
                type="button"
                className="btn-add-param-link"
                onClick={handleAddCustomField}
              >
                <Icon name="plus" size={12} color="#2563EB" />
                <span>Add parameter</span>
              </button>
            </div>
            <div className="custom-params-list">
              {customFields.map((cf, idx) => (
                <div key={idx} className="custom-param-row">
                  <input
                    type="text"
                    placeholder="Key (e.g. timeout)"
                    className="form-input"
                    value={cf.key}
                    onChange={(e) => handleCustomFieldChange(idx, "key", e.target.value)}
                  />
                  <input
                    type="text"
                    placeholder="Value"
                    className="form-input"
                    value={cf.value}
                    onChange={(e) => handleCustomFieldChange(idx, "value", e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn-remove-param"
                    onClick={() => handleRemoveCustomField(idx)}
                    title="Remove parameter"
                  >
                    <Icon name="trash" size={13} color="#DC2626" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Webhook Provisioning Toggle (Only on create) */}
        {!isEditing && (
          <label className="webhook-toggle-row">
            <input
              type="checkbox"
              checked={generateToken}
              onChange={(e) => setGenerateToken(e.target.checked)}
            />
            <div className="toggle-label-content">
              <span className="toggle-title">Provision incoming webhook endpoint</span>
              <span className="toggle-subtitle">
                Generates a secure destination URL with SHA-256 token verification
              </span>
            </div>
          </label>
        )}

        {/* Footer Actions */}
        <div className="modal-form-actions">
          <button
            type="button"
            className="btn-test-connection"
            onClick={handleManualTest}
            disabled={saving || testingConnection}
            title="Test connection credentials before saving"
          >
            <Icon name={testingConnection ? "refresh" : "zap"} size={13} />
            <span>{testingConnection ? "Testing..." : "Test Connection"}</span>
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={saving || testingConnection}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={saving || testingConnection}
            >
              <Icon name={saving ? "refresh" : "check"} size={14} />
              {saving
                ? "Verifying & Saving..."
                : isEditing
                ? "Save Changes"
                : "Connect Integration"}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
